import { useState, useEffect } from 'react';
import { api, HelpTip, PanelHowTo } from './_barrel.js';


        // Effective ultimate beneficial ownership: the people (or the deepest
        // company we can see) who ultimately own this business, computed by
        // following PSC ownership bands down the corporate chain and multiplying.
        // Reads the persisted graph — if it's empty, the Entity graph panel's
        // Rebuild projects the PSC edges this needs.
        const BASIS = [
            ['low', 'Minimum', 'Companies House bands mean “at least X%”. The lower bound is the defensible figure to quote.'],
            ['mid', 'Midpoint', 'The middle of each band — a central estimate.'],
            ['high', 'Maximum', 'The upper bound — worst-case ownership concentration.'],
        ];

        function BusinessUltimateOwners({ token, businessId, hasCompany }) {
            const [basis, setBasis] = useState('low');
            const [rows, setRows] = useState(null);
            const [loading, setLoading] = useState(true);

            const load = async (b) => {
                setLoading(true);
                try {
                    const r = await api(`/api/v1/businesses/${businessId}/ubo?basis=${b}`, {}, token);
                    setRows((r && r.ubo) || []);
                } catch (e) { setRows([]); } finally { setLoading(false); }
            };
            useEffect(() => { load(basis); }, [businessId, token, basis]);

            // This reads the persisted entity graph, which the Ownership & Control
            // panel rebuilds server-side once its own auto-load lands — re-fetch
            // once a few seconds after mount to pick that up without the analyst
            // needing to manually refresh.
            useEffect(() => {
                if (!hasCompany) return;
                const t = setTimeout(() => { load(basis); }, 4000);
                return () => clearTimeout(t);
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [businessId, hasCompany]);

            if (!hasCompany) return null;

            const prefix = basis === 'low' ? '≥ ' : '';
            const people = (rows || []).filter((r) => r.is_ultimate);

            return (
              <div className="panel mb-6">
                <div className="panel-header flex justify-between items-center">
                  <div>
                    <div className="font-display text-xl gold">Ultimate Beneficial Ownership
                      <HelpTip text="Who ultimately owns this company — the real people behind it. Companies House states ownership only in bands (25–50 / 50–75 / 75–100%); where an owner is itself a company, we follow that company's own PSCs and multiply the bands down the chain to the natural persons. Effective % is that multiplied-down figure. Computed from the persisted PSC edges — press Rebuild on the Entity graph panel if this is empty." />
                    </div>
                    <div className="text-xs text-muted font-mono mt-1">Effective ownership through the corporate chain</div>
                  </div>
                  <div className="flex" style={{ gap: '4px' }}>
                    {BASIS.map(([k, lbl, tip]) => (
                      <button key={k} title={tip} onClick={() => setBasis(k)}
                        className="font-mono"
                        style={{ fontSize: '11px', padding: '4px 9px', borderRadius: '4px',
                          border: '1px solid var(--line)',
                          background: basis === k ? 'var(--gold)' : 'transparent',
                          color: basis === k ? '#10151d' : 'var(--text-muted)',
                          fontWeight: basis === k ? 700 : 400, cursor: 'pointer' }}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
                <PanelHowTo id="business_ubo" />
                <div className="px-6 py-5">
                  {loading && <div className="text-sm text-muted">Loading…</div>}
                  {!loading && (rows || []).length === 0 && (
                    <div className="text-sm text-muted" style={{ maxWidth: 640 }}>
                      No ownership chain resolved yet — the Ownership &amp; Control panel below loads
                      automatically and projects this into the graph within a few seconds. If it's
                      still empty after that, this company has no PSC on the Companies House register.
                    </div>
                  )}
                  {!loading && (rows || []).length > 0 && (
                    <>
                      {people.length > 0 && (
                        <div className="text-xs text-muted font-mono mb-3">
                          {people.length} ultimate beneficial owner{people.length === 1 ? '' : 's'} identified
                          {people.some((p) => p.effective_pct >= 25)
                            ? ' · at least one holds a controlling stake (≥25%)' : ''}
                        </div>
                      )}
                      {(rows || []).map((r, i) => (
                        <div key={i} className="table-row px-4 py-3"
                          style={r.is_ultimate ? { borderLeft: '3px solid var(--gold)' } : {}}>
                          <div className="flex justify-between items-start gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm">
                                {r.name}
                                <span className="text-xs text-muted ml-2">
                                  {r.is_ultimate ? '· person' : '· corporate (opaque)'}
                                </span>
                              </div>
                              <div className="text-xs text-muted font-mono mt-1">{r.note}</div>
                            </div>
                            <div className="font-display" style={{ fontSize: '18px', whiteSpace: 'nowrap',
                              color: r.effective_pct > 0 ? 'var(--gold)' : 'var(--text-muted)' }}>
                              {r.effective_pct > 0 ? `${prefix}${r.effective_pct}%` : '—'}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="text-xs text-muted font-mono mt-3" style={{ opacity: 0.75 }}>
                        {BASIS.find(([k]) => k === basis)[2]}
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
        }

export { BusinessUltimateOwners };
